import './CustomerInsightsChart.scss';
import cn from 'classnames';
import { ReactElement, useMemo, useRef, useState } from 'react';
import { Cell, Legend, Pie, PieChart } from 'recharts';
import { formatNumber, formatPercent } from '@blisspointmedia/bpm-types/dist/formatUtils';
import { KnowYourCustomerData } from '@blisspointmedia/bpm-types/dist/KnowYourCustomer';
import { ChartContainer } from '@Components/ChartContainer';
import { useContainerSize } from '../../utils/hooks/useContainerSize';
import { ChartContainerRightActions } from './ChartContainerRightActions';
import {
  CHART_TYPE_MAP,
  ChartType,
  CUSTOMER_INSIGHTS_PRETTY_NAMES,
  CustomerInsightsFields
} from './customer-insights-constants';
import { buildColorMap } from './customer-insights-utils';
import { CustomerInsightsLegend } from './CustomerInsightsLegend';

interface DefaultBarProps {
  data: KnowYourCustomerData[];
  fieldPrettyName: string;
  chartContainerRightActions: ReactElement;
}

const DefaultBar = ({ data, fieldPrettyName, chartContainerRightActions }: DefaultBarProps) => {
  const [hoveredValue, setHoveredValue] = useState<string | undefined>();
  const barMax = Math.ceil(Math.max(...data.map(val => val.clientPopulation)));
  const colorMap = useMemo(() => buildColorMap(data), [data]);

  const legendFormattedData = useMemo(
    () =>
      data.map(({ levels, clientPopulation, clientPopulationFraction }) => ({
        name: levels,
        val1: formatNumber(Math.round(clientPopulation)),
        computedValue: formatPercent(clientPopulationFraction, 1)
      })),
    [data]
  );

  return (
    <div className='customer-insights-bar-chart customer-base-bar-chart'>
      <ChartContainer title={fieldPrettyName} rightActions={chartContainerRightActions}>
        {data.map(({ levels, clientPopulation }, index) => (
          <div
            key={levels}
            className={cn('customer-base-bar-container', {
              'hovered-value': levels === hoveredValue,
              'unhovered-value': Boolean(hoveredValue) && levels !== hoveredValue
            })}
            onMouseEnter={() => setHoveredValue(levels)}
            onMouseLeave={() => setHoveredValue(undefined)}
          >
            <CustomerInsightsLegend
              data={[legendFormattedData[index]]}
              hoveredValue={hoveredValue}
              setHoveredValue={setHoveredValue}
            />
            <div className='customer-base-bars'>
              <div
                className='customer-base-bar'
                style={{
                  width: `${(clientPopulation / barMax) * 100}%`,
                  backgroundColor: colorMap[levels]
                }}
              />
            </div>
          </div>
        ))}
      </ChartContainer>
    </div>
  );
};

interface DefaultPieProps {
  data: KnowYourCustomerData[];
  fieldPrettyName: string;
  chartContainerRightActions: ReactElement;
}

const DefaultPie = ({ data, fieldPrettyName, chartContainerRightActions }: DefaultPieProps) => {
  const [hoveredValue, setHoveredValue] = useState<string | undefined>();
  const sizeRef = useRef<HTMLDivElement>(null);
  const pieSize = useContainerSize(sizeRef);
  const colorMap = useMemo(() => buildColorMap(data), [data]);

  const pieFormattedData = useMemo(
    () =>
      data.map(({ levels, clientPopulation }) => ({
        name: levels,
        value: Math.round(clientPopulation)
      })),
    [data]
  );

  const legendFormattedData = useMemo(
    () =>
      data.map(({ levels, clientPopulation, clientPopulationFraction }) => ({
        name: levels,
        val1: formatNumber(clientPopulation, 0),
        computedValue: formatPercent(clientPopulationFraction, 1),
        color: colorMap[levels]
      })),
    [colorMap, data]
  );

  return (
    <div className='customer-insights-pie-chart customer-base-pie-chart'>
      <ChartContainer title={fieldPrettyName} rightActions={chartContainerRightActions}>
        <div ref={sizeRef} className='pie-sizer'>
          {pieSize && (
            <PieChart width={pieSize.width} height={pieSize.height}>
              <Legend
                content={
                  <CustomerInsightsLegend
                    data={legendFormattedData}
                    hoveredValue={hoveredValue}
                    setHoveredValue={setHoveredValue}
                  />
                }
                layout='vertical'
                align='left'
                verticalAlign='top'
              />
              <Pie
                data={pieFormattedData}
                isAnimationActive={false}
                startAngle={90}
                endAngle={-270}
                cx='50%'
                cy='50%'
                innerRadius='25%'
                dataKey='value'
                onMouseEnter={slice => {
                  setHoveredValue(slice?.name);
                }}
                onMouseLeave={() => {
                  setHoveredValue(undefined);
                }}
              >
                {pieFormattedData.map(({ name }) => (
                  <Cell
                    className={cn('pie-cell', {
                      'hovered-value': name === hoveredValue,
                      'unhovered-value': Boolean(hoveredValue) && name !== hoveredValue
                    })}
                    key={`cell-${name}`}
                    fill={colorMap[name]}
                  />
                ))}
              </Pie>
            </PieChart>
          )}
        </div>
      </ChartContainer>
    </div>
  );
};

interface CustomerBaseChartProps {
  fieldName: CustomerInsightsFields;
  data: KnowYourCustomerData[];
}

export const CustomerBaseChart = ({ fieldName, data }: CustomerBaseChartProps) => {
  const fieldPrettyName = CUSTOMER_INSIGHTS_PRETTY_NAMES[fieldName];
  const chartType = CHART_TYPE_MAP[fieldName];

  if (chartType === ChartType.BAR) {
    return (
      <DefaultBar
        data={data}
        fieldPrettyName={fieldPrettyName}
        chartContainerRightActions={
          <ChartContainerRightActions fieldName={fieldName} data={data} />
        }
      />
    );
  }

  if (chartType === ChartType.PIE) {
    return (
      <DefaultPie
        data={data}
        fieldPrettyName={fieldPrettyName}
        chartContainerRightActions={
          <ChartContainerRightActions fieldName={fieldName} data={data} />
        }
      />
    );
  }

  return null;
};
